import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { bookingApi } from '../api/booking.api'
import { Modal, Badge, Select, Textarea } from '@mantine/core'
import {
  formatAddress,
  formatPhoneNumber,
  getBadgeStyle,
  getLabelStyle,
  getRowStyle,
  getValueStyle,
  getCloseButtonStyle,
  getModalContentStyle,
  getModalContainerStyle,
  getModalTitleStyle,
  getTwoColumnContainerStyle,
  getColumnStyle,
  bookingStatusList,
  BookingStatus,
  getAdminCommentSaveButtonStyle,
  getAdminCommentTextareaStyle,
  getModalContentMobileStyle,
  getModalContainerMobileStyle,
  getModalTitleMobileStyle,
  getTwoColumnContainerMobileStyle,
  getColumnMobileStyle,
  getRowMobileStyle,
  getLabelMobileStyle,
  getValueMobileStyle,
  getAdminCommentTextareaMobileStyle,
  getAdminCommentSaveButtonMobileStyle,
  getCloseButtonMobileStyle,
  getCopyButtonStyle,
  getCopyButtonMobileStyle,
} from '../utils'

interface OrderDetailModalProps {
  bookingId: string | null
  opened: boolean
  onClose: () => void
}

export default function OrderDetailModal({ bookingId, opened, onClose }: OrderDetailModalProps) {
  const [isStatusListOpen, setIsStatusListOpen] = useState<boolean>(false)
  const [adminComment, setAdminComment] = useState<string>('')
  const [adminComments, setAdminComments] = useState<string[]>([])
  const [isMobile, setIsMobile] = useState<boolean>(false)

  const queryClient = useQueryClient()
  const { data, refetch } = useQuery({
    queryKey: ['booking', bookingId],
    queryFn: async () => bookingApi.getById(bookingId as string),
    enabled: opened && !!bookingId,
  })

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const mutation = useMutation({
    mutationFn: ({ status, adminComments }: { status: BookingStatus; adminComments?: string[] }) => {
      const comments = adminComments ?? []
      return bookingApi.update(bookingId as string, status, comments)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking', bookingId] })
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
      refetch()
    },
    onError: error => {
      console.error('API Error: ', error)
    },
  })

  const updateBooking = (status?: BookingStatus, adminComments?: string[]) => {
    const updateData = {
      status: status ?? (data?.status as BookingStatus),
      adminComments: adminComments ?? data?.adminComments ?? [],
    }
    mutation.mutate(updateData)
  }

  const copyPhoneNumber = () => {
    if (!data) return
    navigator.clipboard.writeText(data.phoneNumber)
    alert(`✅ Phone# Copied! ✅`)
  }

  const copyBookingDataForCustomer = () => {
    if (!data) return

    const dataToCopy = `Hi ${data?.firstName} ${data?.lastName}! 👋

Thank you for choosing Canadian Coin Laundry! 🧺

Here are your order details:

📋 Order Details:
Order Number: ${data?.orderNumber}
Phone: ${formatPhoneNumber(data?.phoneNumber)}
Email: ${data?.email}
Address: ${formatAddress(data?.address)}
${data?.description ? `Special Request: ${data?.description}` : ''}

🚀 We're working on your order! No action needed from you.

📞 We'll contact you if there are any updates.

Thanks for trusting us! 💙`
    navigator.clipboard.writeText(dataToCopy)
    alert(`✅ Message Copied and Ready! ✅`)
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size='md'
      withCloseButton={false}
      styles={{
        content: isMobile ? getModalContentMobileStyle() : getModalContentStyle(),
      }}>
      <div style={isMobile ? getModalContainerMobileStyle() : getModalContainerStyle()}>
        {isMobile ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <span style={getValueMobileStyle('default')}>{data?.orderNumber}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <span style={getValueMobileStyle('default')}>
                {data?.firstName} {data?.lastName}
              </span>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <Badge
                  style={getBadgeStyle(data?.status || BookingStatus.NEW)}
                  variant='filled'
                  size='md'
                  onClick={() => setIsStatusListOpen(!isStatusListOpen)}>
                  {data?.status}
                </Badge>
                {isStatusListOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      zIndex: 1000,
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      minWidth: '100px',
                    }}>
                    {bookingStatusList.map(status => (
                      <div
                        key={status.value}
                        onClick={() => {
                          setIsStatusListOpen(false)
                          updateBooking(status.value, data?.adminComments)
                        }}
                        style={{
                          padding: '6px 8px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #eee',
                          fontSize: '12px',
                        }}>
                        {status.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <span style={getValueStyle('default')}>{data?.orderNumber}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <span style={getValueStyle('default')}>
                {data?.firstName} {data?.lastName}
              </span>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <Badge
                  style={getBadgeStyle(data?.status || BookingStatus.NEW)}
                  variant='filled'
                  size='md'
                  onClick={() => setIsStatusListOpen(!isStatusListOpen)}>
                  {data?.status}
                </Badge>
                {isStatusListOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      zIndex: 1000,
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      minWidth: '120px',
                    }}>
                    {bookingStatusList.map(status => (
                      <div
                        key={status.value}
                        onClick={() => {
                          setIsStatusListOpen(false)
                          updateBooking(status.value, data?.adminComments)
                        }}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #eee',
                          fontSize: '14px',
                        }}>
                        {status.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <hr />

        <div style={{ marginBottom: isMobile ? '20px' : '30px' }}>
          {isMobile ? (
            <>
              <div style={getTwoColumnContainerStyle()}>
                <div style={{ flex: 1 }}>
                  <div style={getRowStyle()}>
                    <span style={getLabelMobileStyle()}>Phone:</span>
                    <span style={getValueMobileStyle('default')}>{formatPhoneNumber(data?.phoneNumber)}</span>
                  </div>
                  <div style={getRowStyle()}>
                    <span style={getLabelMobileStyle()}>Email:</span>
                    <span style={getValueMobileStyle('email')}>{data?.email}</span>
                  </div>
                  <div style={getRowStyle()}>
                    <span style={getLabelMobileStyle()}>Address:</span>
                    <span style={getValueMobileStyle('default')}>
                      {data?.address ? formatAddress(data.address) : 'No address provided'}
                    </span>
                  </div>
                  <div style={getRowStyle()}>
                    <span style={getLabelMobileStyle()}>Pickup Date:</span>
                    <span style={getValueMobileStyle('default')}>
                      {data?.pickupDate ? new Date(data.pickupDate).toLocaleDateString() : ''}
                    </span>
                  </div>
                  <div style={getRowStyle()}>
                    <span style={getLabelMobileStyle()}>Pickup Time:</span>
                    <span style={getValueMobileStyle('default')}>{data?.pickupTime}</span>
                  </div>
                </div>
              </div>

              {data?.description && (
                <div style={{ ...getRowStyle(), alignItems: 'flex-start' }}>
                  <span style={getLabelMobileStyle()}>Description:</span>
                  <span style={getValueMobileStyle('default')}>{data.description}</span>
                </div>
              )}
            </>
          ) : (
            <>
              <div style={getTwoColumnContainerStyle()}>
                <div style={getColumnStyle()}>
                  <div style={getRowStyle()}>
                    <span style={getLabelStyle()}>Phone:</span>
                    <span style={getValueStyle('default')}>{formatPhoneNumber(data?.phoneNumber)}</span>
                  </div>

                  <div style={getRowStyle()}>
                    <span style={getLabelStyle()}>Pickup Date:</span>
                    <span style={getValueStyle('default')}>
                      {data?.pickupDate ? new Date(data.pickupDate).toLocaleDateString() : ''}
                    </span>
                  </div>
                </div>

                <div style={{ flex: 1 }}>
                  <div style={getRowStyle()}>
                    <span style={getLabelStyle()}>Email:</span>
                    <span style={getValueStyle('email')}>{data?.email}</span>
                  </div>
                  <div style={getRowStyle()}>
                    <span style={getLabelStyle()}>Pickup Time:</span>
                    <span style={getValueStyle('default')}>{data?.pickupTime}</span>
                  </div>
                </div>
              </div>

              <div style={getRowStyle()}>
                <span style={getLabelStyle()}>Address:</span>
                <span style={getValueStyle('default')}>
                  {data?.address ? formatAddress(data.address) : 'No address provided'}
                </span>
              </div>

              {data?.description && (
                <div style={{ ...getRowStyle(), alignItems: 'flex-start' }}>
                  <span style={getLabelStyle()}>Description:</span>
                  <span style={getValueStyle('default')}>{data.description}</span>
                </div>
              )}
            </>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: '20px',
          }}>
          <button
            onClick={copyPhoneNumber}
            style={{
              background: 'none',
              border: 'none',
              paddingRight: '14px',
            }}>
            ☎️
          </button>
          <button
            onClick={copyBookingDataForCustomer}
            style={isMobile ? getCopyButtonMobileStyle() : getCopyButtonStyle()}>
            <svg
              width='16'
              height='16'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              style={{ display: 'inline-block', verticalAlign: 'middle' }}>
              <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
              <polyline points='7,10 12,15 17,10' />
              <line x1='12' y1='15' x2='12' y2='3' />
            </svg>
          </button>
        </div>
        <hr />
        <div style={{ marginBottom: '20px' }}>
          <span style={isMobile ? getLabelMobileStyle() : getLabelStyle()}>Admin Comment:</span>
          <div style={{ marginTop: '5px' }}>
            <div
              style={{
                display: 'flex',
                gap: isMobile ? '15px' : '20px',
                alignItems: 'flex-start',
                flexDirection: isMobile ? 'column' : 'row',
              }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', width: isMobile ? '100%' : 'auto' }}>
                {isMobile ? (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '5px' }}>
                    <Textarea
                      placeholder='우리들만의 비밀...'
                      value={adminComment}
                      onChange={event => setAdminComment(event.currentTarget.value)}
                      onKeyDown={event => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                        }
                      }}
                      minRows={1}
                      maxRows={1}
                      resize='none'
                      autosize={false}
                      styles={{
                        input: {
                          ...getAdminCommentTextareaMobileStyle(),
                          height: '18px',
                          border: '1px solid #000',
                          fontSize: '18px',
                        },
                      }}
                      style={{
                        marginBottom: '5px',
                        width: '100%',
                      }}
                    />
                    <button
                      onClick={() => {
                        const existingComments = data?.adminComments || []
                        const newComment = adminComment.trim()
                        if (newComment) {
                          updateBooking(undefined, [...existingComments, newComment])
                          setAdminComment('')
                        }
                      }}
                      style={{
                        ...getAdminCommentSaveButtonMobileStyle(),
                        height: '24px',
                        minWidth: 'auto',
                        width: '24px',
                        padding: '0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: 'bold',
                      }}>
                      OK
                    </button>
                  </div>
                ) : (
                  <>
                    <Textarea
                      placeholder='우리들만의 비밀...'
                      value={adminComment}
                      onChange={event => setAdminComment(event.currentTarget.value)}
                      minRows={3}
                      maxRows={3}
                      resize='none'
                      autosize={false}
                      styles={{
                        input: getAdminCommentTextareaStyle(),
                      }}
                      style={{
                        marginBottom: '5px',
                        width: '100%',
                      }}
                    />
                    <button
                      onClick={() => {
                        const existingComments = data?.adminComments || []
                        const newComment = adminComment.trim()
                        if (newComment) {
                          updateBooking(undefined, [...existingComments, newComment])
                          setAdminComment('')
                        }
                      }}
                      style={getAdminCommentSaveButtonStyle()}>
                      Save
                    </button>
                  </>
                )}
              </div>
              {data?.adminComments && (
                <div style={{ flex: 1, overflowY: 'auto', width: isMobile ? '100%' : 'auto' }}>
                  {data.adminComments.map((comment, index) => (
                    <div key={index} style={{ marginBottom: '5px' }}>
                      <span style={isMobile ? getValueMobileStyle('default') : getValueStyle('default')}>
                        {comment}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <button onClick={onClose} style={isMobile ? getCloseButtonMobileStyle() : getCloseButtonStyle()}>
          Close
        </button>
      </div>
    </Modal>
  )
}
